import {
  segmentedPillContainerClasses,
  segmentedPillDividerClasses,
  segmentedPillDividerHideOnHoverClasses,
  segmentedPillHoverPillClasses,
} from "@ui/components/internal/SegmentedPill";
import { tv, type VariantProps } from "@ui/lib/tv";
import { composeRenderProps } from "@ui/primitives/shared";
import {
  ToggleButton as RACToggleButton,
  type ToggleButtonProps as RACToggleButtonProps,
} from "@ui/primitives/ToggleButton";
import {
  ToggleButtonGroup as RACToggleButtonGroup,
  type ToggleButtonGroupProps as RACToggleButtonGroupProps,
  SelectionIndicator,
} from "@ui/primitives/ToggleButtonGroup";

export const buttonGroup = tv({
  base: ["group/btngroup relative flex items-stretch overflow-clip rounded-[16px]", segmentedPillContainerClasses],
  defaultVariants: { size: "md" },
  variants: {
    size: {
      // In-form scale — the md/lg/xl steps are sized for full-page search chrome
      // and overpower a dialog body.
      sm: "h-[40px] rounded-[12px]",
      md: "h-[52px]",
      lg: "h-[56px]",
      xl: "h-[60px]",
    },
  },
});

export const buttonGroupItem = tv({
  base: [
    "group/btnitem relative flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-[12px] outline-none",
    "text-neutral-900",
    "transition-colors",
    "text-sm group-data-[size=lg]/btngroup:text-base group-data-[size=xl]/btngroup:text-base",
    "group-data-[size=sm]/btngroup:rounded-[10px] group-data-[size=sm]/btngroup:before:rounded-[10px]",
    "selected:font-semibold selected:text-red-400",
    "focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2",
    segmentedPillHoverPillClasses,
    "before:right-0.5 before:left-0.5 before:rounded-[12px] before:bg-red-50",
    "first:before:left-0 last:before:right-0",
    "selected:before:opacity-0 hover:before:opacity-100",
    segmentedPillDividerClasses,
    "after:right-0 after:h-6 after:bg-neutral-200",
    segmentedPillDividerHideOnHoverClasses,
    "selected:after:opacity-0 has-[+[data-selected]]:after:opacity-0",
  ],
});

export const buttonGroupIndicator = tv({
  base: [
    "absolute inset-y-0 right-0.5 left-0.5 rounded-[12px] border-2 border-red-400 bg-red-50",
    "group-data-[size=sm]/btngroup:rounded-[10px]",
    "group-first/btnitem:left-0 group-last/btnitem:right-0",
    "motion-safe:transition-[translate,width,height] motion-safe:duration-200",
  ],
});

type ButtonGroupVariants = VariantProps<typeof buttonGroup>;

export interface ButtonGroupProps extends RACToggleButtonGroupProps {
  size?: ButtonGroupVariants["size"];
}

export function ButtonGroup({ size = "md", ...props }: ButtonGroupProps) {
  return (
    <RACToggleButtonGroup
      {...props}
      data-size={size}
      className={composeRenderProps(props.className, (className) => buttonGroup({ size, className }))}
    />
  );
}

export interface ButtonGroupItemProps extends RACToggleButtonProps {}

export function ButtonGroupItem(props: ButtonGroupItemProps) {
  return (
    <RACToggleButton
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        buttonGroupItem({ ...renderProps, className }),
      )}
    >
      {composeRenderProps(props.children, (children) => (
        <>
          <SelectionIndicator className={buttonGroupIndicator()} />
          <span className="relative z-10">{children}</span>
        </>
      ))}
    </RACToggleButton>
  );
}
